import React from 'react';
import { BiChevronDown, BiChevronUp, BiPlus } from 'react-icons/bi';
import { FaClipboardCheck } from 'react-icons/fa';
import { FiBook, FiTrash2 } from 'react-icons/fi';
// import { Book, ClipboardCheck, ChevronUp, ChevronDown, Trash2, Plus } from 'lucide-react';

interface PathItem {
  id: string;
  title: string;
  description?: string;
  duration: string;
  type: 'course' | 'assessment';
  questions?: number;
}

interface LearningPathCanvasProps {
  pathItems: PathItem[];
  onRemoveItem: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  onDrop: (item: PathItem) => void;
}

const LearningPathCanvas: React.FC<LearningPathCanvasProps> = ({
  pathItems,
  onRemoveItem,
  onMoveUp,
  onMoveDown,
  onDrop
}) => {
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const itemData = e.dataTransfer.getData('application/json');
    if (itemData) {
      const item = JSON.parse(itemData);
      onDrop(item);
    }
  };

  return (
    <div className="flex-1 bg-white flex flex-col">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Learning Path Sequence</h2>
        <p className="text-gray-600 mt-1">Drag items from the left to build your learning path</p>
      </div>

      <div className="flex-1 p-6 overflow-y-auto">
        <div className="space-y-4" onDragOver={handleDragOver} onDrop={handleDrop}>
          {pathItems.map((item, index) => (
            <div
              key={item.id}
              className={`flex items-center rounded-lg p-4 ${
                item.type === 'course'
                  ? 'bg-blue-50 border border-blue-200'
                  : 'bg-purple-50 border border-purple-200'
              }`}
            >
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium mr-4 text-white ${
                item.type === 'course' ? 'bg-customOrange' : 'bg-darkBlue'
              }`}>
                {index + 1}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  {item.type === 'course' ? (
                    <>
                      <FiBook size={16} className="text-customOrange mr-2" />
                      <span className="text-sm font-medium text-gray-900">COURSE</span>
                    </>
                  ) : (
                    <>
                      <FaClipboardCheck size={16} className="text-darkBlue mr-2" />
                      <span className="text-sm font-medium text-darkBlue">ASSESSMENT</span>
                    </>
                  )}
                </div>
                <h3 className="font-medium text-gray-900">{item.title}</h3>
                <p className="text-sm text-gray-600">
                  {item.questions ? `${item.questions} questions • ` : ''}{item.duration}
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onMoveUp(item.id)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={index === 0}
                >
                  <BiChevronUp size={16} />
                </button>
                <button
                  onClick={() => onMoveDown(item.id)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={index === pathItems.length - 1}
                >
                  <BiChevronDown size={16} />
                </button>
                <button
                  onClick={() => onRemoveItem(item.id)}
                  className="text-gray-400 hover:text-red-600"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
            </div>
          ))}

          {/* Drop Zone */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
            <BiPlus size={24} className="mx-auto mb-2" />
            <p>Drop courses or assessments here to add them to your learning path</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningPathCanvas;